import { useEffect } from 'react';
import type React from 'react';

import { once } from 'lodash-es';

import { Button, Separator, Text, ThemeProvider } from '@change/ds-core-ui';

import type { PerimeterXEnforcerChallengeWithBlockedUrl } from '@change-corgi/core/perimeterx';
import { getWindow } from '@change-corgi/core/window';
import { Translate } from '@change-corgi/core-react/i18n';

import { PX_FAKE_CHALLENGE_APP_ID } from '#src/app/shared/utils/perimeterx';

// This component renders an in-line <div> whose contents are populated by a
// script included from our vendor PerimeterX, presenting a captcha (Google
// reCAPTCHA) challenge to users suspected of being bots. When the captcha is
// completed by the user, onSuccess() or onFailure() will be called as
// appropriate.
//
// See the PerimeterX sample implementation here:
// https://github.com/PerimeterX/perimeterx-abr-samples

declare global {
	// oxlint-disable-next-line typescript/consistent-type-definitions
	interface Window {
		// eslint-disable-next-line @typescript-eslint/naming-convention
		_pxOnCaptchaSuccess: unknown;
	}
}

type Props = {
	pxResponse: PerimeterXEnforcerChallengeWithBlockedUrl;
	onSuccess?: () => void;
	onFailure?: () => void;
};

export const PerimeterXCaptcha: React.FC<Props> = ({ pxResponse, onSuccess, onFailure }) => {
	// This should set window._pxOnCaptchaSuccess exactly once, and clean it up
	// when the component gets destroyed.
	useEffect(() => {
		const win = getWindow();
		win._pxOnCaptchaSuccess = once((isValid) => {
			if (isValid) onSuccess?.();
			else onFailure?.();
		});
		return () => {
			delete win._pxOnCaptchaSuccess;
		};
	}, []);

	useEffect(() => {
		const win = getWindow() as unknown as Record<string, unknown>;
		win._pxBlockedUrl = pxResponse.blockedUrl;
		win._pxAppId = pxResponse.appId;
		win._pxJsClientSrc = pxResponse.jsClientSrc;
		win._pxFirstPartyEnabled = pxResponse.firstPartyEnabled;
		win._pxVid = pxResponse.vid;
		win._pxUuid = pxResponse.uuid;
		win._pxHostUrl = pxResponse.hostUrl;

		const script = document.createElement('script');
		script.src = pxResponse.blockScript;
		document.head.appendChild(script);
		return () => {
			script.remove();
		};
	}, [pxResponse]);

	return (
		<div>
			<ThemeProvider>
				{/* using tabindex=0 to allow for tabbing inside the iframe (https://github.com/focus-trap/focus-trap-react/issues/459#issuecomment-915585105) */}
				<Text variant="body" as="p" size="medium" className="pb-4" tabIndex={0}>
					<Translate value="fe.components.px_captcha.preamble" />
				</Text>
				<div className="my-6 rounded-lg border p-4 shadow-[0_2px_3px_0_rgba(54,49,53,0.2)]">
					{/* The vendor code specifically looks for a div with this id: */}
					<div id="px-captcha" className="flex justify-center sm:justify-start" tabIndex={0} />
				</div>
				{/* using tabindex=0 to allow for tabbing inside the iframe (https://github.com/focus-trap/focus-trap-react/issues/459#issuecomment-915585105) */}
				<Text variant="body" as="p" size="medium" tabIndex={0}>
					<Translate value="fe.components.px_captcha.helptext" />
				</Text>
				{pxResponse.appId === PX_FAKE_CHALLENGE_APP_ID && (
					<>
						<Separator orientation="horizontal" className="my-2" />
						<Text variant="body" as="p" className="pb-2" tabIndex={0}>
							This looks like a test challenge {'=>'} you can simulate success or failure
						</Text>
						<div className="flex flex-wrap gap-2 pt-2">
							<Button type="button" variant="primary" onClick={() => onSuccess?.()} size="small">
								Simulate success
							</Button>

							<Button type="button" variant="primary" onClick={() => onFailure?.()} size="small">
								Simulate failure
							</Button>
						</div>
						<Separator orientation="horizontal" className="my-2" />
					</>
				)}
				<Text
					variant="body"
					as="p"
					fontWeight="normal"
					className="mt-4 py-4"
					color="typography-secondary"
					size="x-small"
				>
					<Translate value="fe.components.px_captcha.reference_id" /> {pxResponse.uuid}
				</Text>
			</ThemeProvider>
		</div>
	);
};
